import { createContext, useContext, useState, useEffect, useCallback, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import api from "./lib/api";
import Sidebar from "./components/Sidebar";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import CustomersPage from "./pages/CustomersPage";
import SlipsPage from "./pages/SlipsPage";
import CreateSlipPage from "./pages/CreateSlipPage";
import SlipDetailPage from "./pages/SlipDetailPage";
import WorkersPage from "./pages/WorkersPage";
import NotificationsPage from "./pages/NotificationsPage";
import SettingsPage from "./pages/SettingsPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import ScanPage from "./pages/ScanPage";
import WhatsAppPage from "./pages/WhatsAppPage";
import InquiriesPage from "./pages/InquiriesPage";
import LandingPage from "./pages/LandingPage";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

function AuthProvider({ children }) {
  const [admin, setAdmin] = useState(() => {
    const stored = localStorage.getItem("vr_admin");
    return stored ? JSON.parse(stored) : null;
  });
  // loading=true means "actively verifying a stored token with the server"
  const [loading, setLoading] = useState(() => !!localStorage.getItem("vr_token"));

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem("vr_token");
    if (!token) { setLoading(false); return; }
    try {
      const res = await api.get("/auth/me");
      setAdmin(res.data);
      localStorage.setItem("vr_admin", JSON.stringify(res.data));
    } catch {
      setAdmin(null);
      localStorage.removeItem("vr_token");
      localStorage.removeItem("vr_admin");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    // If no token, nothing to verify
    if (!localStorage.getItem("vr_token")) return;
    // Verify token in background — UI is already rendered using cached admin
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    localStorage.setItem("vr_token", res.data.token);
    localStorage.setItem("vr_admin", JSON.stringify(res.data.admin));
    setAdmin(res.data.admin);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem("vr_token");
    localStorage.removeItem("vr_admin");
    setAdmin(null);
  };

  return (
    <AuthContext.Provider value={{ admin, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ children }) {
  const { admin, loading } = useAuth();
  // If we have a cached admin, render immediately (background verification in progress)
  if (admin) return children;
  // No cached admin: show spinner only while actively verifying a stored token
  if (loading) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="animate-pulse gold-text font-serif text-xl">Loading...</div>
    </div>
  );
  return <Navigate to="/" replace />;
}

function DashboardLayout({ children }) {
  return (
    <div className="min-h-screen bg-background flex">
      <Sidebar />
      <main className="flex-1 md:ml-64 min-h-screen pt-14 md:pt-0">
        <div className="p-4 md:p-8 max-w-[1400px]">
          {children}
        </div>
      </main>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/scan/:slipId" element={<ScanPage />} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout><DashboardPage /></DashboardLayout></ProtectedRoute>} />
          <Route path="/customers" element={<ProtectedRoute><DashboardLayout><CustomersPage /></DashboardLayout></ProtectedRoute>} />
          <Route path="/slips" element={<ProtectedRoute><DashboardLayout><SlipsPage /></DashboardLayout></ProtectedRoute>} />
          <Route path="/slips/new" element={<ProtectedRoute><DashboardLayout><CreateSlipPage /></DashboardLayout></ProtectedRoute>} />
          <Route path="/slips/:id" element={<ProtectedRoute><DashboardLayout><SlipDetailPage /></DashboardLayout></ProtectedRoute>} />
          <Route path="/workers" element={<ProtectedRoute><DashboardLayout><WorkersPage /></DashboardLayout></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><DashboardLayout><NotificationsPage /></DashboardLayout></ProtectedRoute>} />
          <Route path="/whatsapp" element={<ProtectedRoute><DashboardLayout><WhatsAppPage /></DashboardLayout></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute><DashboardLayout><AnalyticsPage /></DashboardLayout></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><DashboardLayout><SettingsPage /></DashboardLayout></ProtectedRoute>} />
          <Route path="/inquiries" element={<ProtectedRoute><DashboardLayout><InquiriesPage /></DashboardLayout></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster
        theme="dark"
        position="top-right"
        toastOptions={{
          style: { background: '#1a1a1a', border: '1px solid rgba(212,175,55,0.2)', color: '#f5f0e8' }
        }}
      />
    </AuthProvider>
  );
}

export default App;
